import { NewbornGrowthPanel } from './NewbornGrowthPanel';
import { gaDays } from './calculations';
import { NewbornScreeningPanel } from './NewbornScreeningPanel';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatInstant } from '@/lib/utils/date';
import { ClinicalEventEntry } from './ClinicalEventEntry';
import { NewbornExamEntry } from './NewbornExamEntry';
import {
  maternalPostpartumGroups,
  newbornPostpartumGroups,
  newbornMedications,
  newbornMedicationDescriptor,
  withNewbornDeclines,
  lastMedicationStatus,
} from './postpartumFields';
import { activeBirthEvents, cumulativeBloodLoss, newbornAgeHours, shockIndex } from './birthCalculations';
import type { PracticeConfiguration } from './rules';
import type { ClinicalEpisode, ClinicalEvent, ClinicalSubject } from './types';
import type { ProposedEntry } from './PrenatalEntry';

export interface PostpartumDyadProps {
  edd?:string|null;
  episode: ClinicalEpisode;
  events: ClinicalEvent[];
  encounterId: string;
  timeZone: string;
  configuration?: PracticeConfiguration;
  disabled?: boolean;
  newbornBirths?: Record<string, ClinicalEvent>;
  pendingBirths?: { subject_id: string; encounter_id: string; occurred_at: string; payload: Record<string, unknown> }[];
  onSave: (entries: ProposedEntry[], occurredAt: string) => Promise<void>;
  onEdit?: (event: ClinicalEvent) => void;
}
/** The same component receives the same durable save adapter in warm and cold views. */
export function PostpartumDyad(props: PostpartumDyadProps) {
  const {
    episode,
    events,
    encounterId,
    timeZone,
    disabled,
    onSave,
    onEdit,
    newbornBirths = {},
    pendingBirths = [],
  } = props;
  const [view, setView] = useState('all');
  const [pendingCare, setPendingCare] = useState<Set<string>>(() => new Set());
  const active = activeBirthEvents(events, episode.id, new Date().toISOString()) as ClinicalEvent[];
  const births = active.filter((e) => e.event_type === 'BIRTH' && e.encounter_id === encounterId);
  const mother = episode.subjects.find((s) => s.subject_type === 'MOTHER');
  const newborns = episode.subjects.filter((s) => s.subject_type === 'NEWBORN' && s.active !== false);
  const firstBirth = [...births].sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))[0];
  const choices = [
    { id: 'all', label: 'Mother and babies' },
    ...(mother ? [{ id: mother.id, label: 'Mother' }] : []),
    ...newborns.map((s) => ({ id: s.id, label: s.label })),
  ];
  const chosen = choices.some((v) => v.id === view) ? view : 'all';
  const panels = [mother, ...newborns].filter((s): s is ClinicalSubject => !!s);
  return (
    <div className="flex flex-col gap-4">
      {!births.length && (
        <Alert>
          <AlertDescription>
            Record the birth, including an estimated time when necessary, to link newborn care and calculate age and
            postpartum totals. Maternal observations may still be recorded.
          </AlertDescription>
        </Alert>
      )}
      <Tabs value={chosen} onValueChange={setView}>
        <TabsList className="h-auto flex-wrap">
          {choices.map((c) => (
            <TabsTrigger key={c.id} value={c.id} className="min-h-11">
              {c.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className={chosen === 'all' ? 'grid grid-cols-1 gap-4 xl:grid-cols-2' : 'flex flex-col gap-4'}>
          {panels.map((subject) => {
            const birth =
              subject.subject_type === 'MOTHER'
                ? firstBirth
                : (newbornBirths[subject.id] ?? births.find((e) => e.subject_id === subject.fetus_subject_id));
            const localBirths = pendingBirths.filter(
              (e) => e.encounter_id === encounterId && e.subject_id === subject.fetus_subject_id,
            );
            const localLive = localBirths.length === 1 && localBirths[0].payload.outcome === 'Live birth';
            const eligible =
              subject.subject_type === 'MOTHER' ||
              birth?.payload.outcome === 'Live birth' ||
              (!birth && (localLive || pendingCare.has(subject.id)));
            return (
              <div key={`${encounterId}:${subject.id}`} hidden={chosen !== 'all' && chosen !== subject.id}>
                {localLive && !birth && (
                  <Alert>
                    <AlertDescription>
                      Birth is saved on this device and pending confirmation. Newborn observations will link when
                      synchronized; confirmed age and totals remain unavailable until then.
                    </AlertDescription>
                  </Alert>
                )}
                {subject.subject_type === 'NEWBORN' && !birth && !localLive && !pendingCare.has(subject.id) && (
                  <Alert>
                    <AlertDescription>
                      Birth evidence for {subject.label} is not confirmed on this device. If the birth was recorded
                      elsewhere, you can document neonatal observations now; they will link after synchronization.
                      <Button
                        variant="outline"
                        className="min-h-11"
                        disabled={disabled}
                        onClick={() => setPendingCare((current) => new Set([...current, subject.id]))}>
                        Chart {subject.label} while birth evidence is pending
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
                <SubjectPostpartum
                  subject={subject}
                  episodeId={episode.id}
                  events={events.filter((e) => e.encounter_id === encounterId)}
                  timeZone={timeZone}
                  birthAt={birth?.occurred_at}
                  sex={['MALE','FEMALE'].includes(String(birth?.payload.sex).toUpperCase())?String(birth?.payload.sex).toUpperCase() as 'MALE'|'FEMALE':undefined}
                  gaAtBirthDays={birth?gaDays(formatInstant(birth.occurred_at,'yyyy-MM-dd',timeZone),props.edd)??undefined:undefined}
                  configuration={props.configuration}
                  disabled={disabled || !eligible}
                  onSave={onSave}
                  onEdit={onEdit}
                />
              </div>
            );
          })}
        </div>
      </Tabs>
    </div>
  );
}
function SubjectPostpartum({
  subject,
  episodeId,
  events,
  timeZone,
  birthAt,
  sex,gaAtBirthDays,
  configuration,
  disabled,
  onSave,
  onEdit,
}: {
  subject: ClinicalSubject;
  episodeId: string;
  events: ClinicalEvent[];
  timeZone: string;
  birthAt?: string;
  sex?:'MALE'|'FEMALE';
  gaAtBirthDays?:number;
  configuration?: PracticeConfiguration;
  disabled?: boolean;
  onSave: PostpartumDyadProps['onSave'];
  onEdit?: PostpartumDyadProps['onEdit'];
}) {
  const isMother = subject.subject_type === 'MOTHER';
  const groups = isMother ? maternalPostpartumGroups : newbornPostpartumGroups;
  const [section, setSection] = useState('');
  const confirmed = activeBirthEvents(events, episodeId, new Date().toISOString()) as ClinicalEvent[];
  const rows = postpartumRows(confirmed, subject.id, birthAt);
  const loss = isMother
    ? cumulativeBloodLoss(events, episodeId, subject.id, birthAt ?? null, new Date().toISOString()).value
    : null;
  const save = async (entries: ProposedEntry[], occurredAt: string) => {
    if (entries.some((e) => e.subject_id !== subject.id)) throw new Error('Wrong subject');
    await onSave(isMother ? entries : withNewbornDeclines(entries), occurredAt);
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>{isMother ? 'Mother · immediate postpartum' : subject.label}</CardTitle>
        <CardDescription>
          {birthAt ? `Birth ${formatInstant(birthAt, 'MMM d, yyyy HH:mm:ss', timeZone)}` : 'Birth time not available'}
        </CardDescription>
        {loss && (
          <p>
            Documented cumulative loss:{' '}
            <strong>{loss.total_ml === null ? 'Not quantified' : `${loss.total_ml} mL`}</strong>
            {loss.total_ml !== null &&
              ` · estimated ${loss.ebl_ml} mL · quantified ${loss.qbl_ml} mL${loss.other_ml ? ` · other ${loss.other_ml} mL` : ''}`}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {Object.keys(groups).map((label) => (
            <Button
              type="button"
              key={label}
              variant="outline"
              className="min-h-11"
              aria-pressed={section === label}
              onClick={() => setSection(section === label ? '' : label)}>
              {label}
            </Button>
          ))}
          {!isMother && (
            <>
              <Button
                variant="outline"
                className="min-h-11"
                aria-pressed={section === 'Exam'}
                onClick={() => setSection(section === 'Exam' ? '' : 'Exam')}>
                Newborn exam
              </Button>
              <Button
                variant="outline"
                className="min-h-11"
                aria-pressed={section === 'Newborn medications'}
                onClick={() => setSection(section === 'Newborn medications' ? '' : 'Newborn medications')}>
                Newborn medications
              </Button>
            </>
          )}
        </div>
        {!isMother && (
          <div className="flex flex-wrap gap-2">
            {(['cchd', 'ballard'] as const).map((mode) => (
              <Button
                key={mode}
                variant="outline"
                className="min-h-11"
                onClick={() => setSection(section === mode ? '' : mode)}>
                {mode === 'cchd' ? 'CCHD screening' : 'New Ballard score'}
              </Button>
            ))}
          </div>
        )}
        {!isMother &&
          (['cchd', 'ballard'] as const).map((mode) => (
            <div key={mode} hidden={section !== mode}>
              <NewbornScreeningPanel
                mode={mode}
                subject={subject}
                birthAt={birthAt}
                events={events}
                episodeId={episodeId}
                timeZone={timeZone}
                configuration={configuration}
                disabled={disabled}
                onSave={save}
              />
            </div>
          ))}
        {!isMother&&<NewbornGrowthPanel subject={subject} episodeId={episodeId} events={events} birthAt={birthAt} sex={sex} gaAtBirthDays={gaAtBirthDays} configuration={configuration} timeZone={timeZone}/>}
        {Object.entries(groups).map(([label, descriptors]) => (
          <div key={label} hidden={section !== label}>
            <ClinicalEventEntry
              title={`${subject.label}: ${label}`}
              descriptors={descriptors}
              subjects={[subject]}
              timeZone={timeZone}
              events={events}
              disabled={disabled}
              onSave={save}
            />
          </div>
        ))}
        {!isMother && (
          <div hidden={section !== 'Exam'}>
            <NewbornExamEntry
              key={subject.id}
              subjectId={subject.id}
              subjectLabel={subject.label}
              timeZone={timeZone}
              disabled={disabled}
              onSave={save}
            />
          </div>
        )}
        {!isMother && (
          <div hidden={section !== 'Newborn medications'}>
            <div className="flex flex-col gap-4">
              <p>
                Record the administered medication or an explicit decision. Dose, route, and lot are entered from the
                administration record.
              </p>
              {newbornMedications.map((drug) => (
                <div key={drug} className="flex flex-col gap-2">
                  <Badge variant="secondary">
                    {drug}: {lastMedicationStatus(events, subject.id, drug)}
                  </Badge>
                  <ClinicalEventEntry
                    descriptors={[newbornMedicationDescriptor(drug)]}
                    subjects={[subject]}
                    timeZone={timeZone}
                    events={events}
                    disabled={disabled}
                    onSave={save}
                    title={drug}
                  />
                </div>
              ))}
              <p>
                Declined or deferred procedures create a linked consent decision. Signed form evidence can be attached
                in Consent review.
              </p>
            </div>
          </div>
        )}
        {isMother && (
          <p>
            Enter additional blood loss for each observation; a repeated cumulative estimate would be counted again.
          </p>
        )}
        <div className="max-h-[32rem] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                {!isMother && <TableHead>Age (h)</TableHead>}
                <TableHead>{isMother ? 'BP / pulse / temp' : 'HR / RR / temp / SpO₂'}</TableHead>
                <TableHead>{isMother ? 'Fundus / perineum' : 'Color / location'}</TableHead>
                <TableHead>{isMother ? 'Loss / cumulative' : 'Feeding / output'}</TableHead>
                <TableHead>Other findings</TableHead>
                <TableHead>Initials / review</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>{formatInstant(row.at, 'HH:mm:ss', timeZone)}</TableCell>
                  {!isMother && (
                    <TableCell>{birthAt ? (newbornAgeHours(row.at, birthAt).value ?? '—') : '—'}</TableCell>
                  )}
                  <TableCell>
                    {row.vitals}
                    {isMother && row.shock !== null && <p>Shock index {row.shock.toFixed(2)}</p>}
                  </TableCell>
                  <TableCell>{isMother ? row.fundus : row.color}</TableCell>
                  <TableCell>
                    {isMother ? (
                      <>
                        {row.loss}
                        {row.total !== null && <p>Σ {row.total} mL</p>}
                      </>
                    ) : (
                      row.feeding
                    )}
                  </TableCell>
                  <TableCell>
                    {row.events
                      .filter(
                        (e) =>
                          e.event_type.startsWith('NB.') ||
                          ['PP.STATUS', 'NOTE.PROGRESS', 'MED.ADMIN'].includes(e.event_type),
                      )
                      .map((e) => (
                        <p key={e.id}>
                          {e.event_type.replaceAll('_', ' ')}: {readableFinding(e.payload)}
                        </p>
                      ))}
                  </TableCell>
                  <TableCell>
                    {row.initials}
                    {onEdit &&
                      row.events.map((e) => (
                        <Button key={e.id} variant="ghost" className="min-h-11" onClick={() => onEdit(e)}>
                          {e.event_type} · review
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!rows.length && <p>No confirmed postpartum observations recorded.</p>}
        </div>
      </CardContent>
    </Card>
  );
}
export function postpartumRows(events: ClinicalEvent[], subjectId: string, birthAt?: string) {
  const relevant = events.filter(
    (e) =>
      e.subject_id === subjectId &&
      (e.event_type.startsWith('PP.') ||
        e.event_type.startsWith('NB.') ||
        e.event_type.startsWith('VITALS.') ||
        e.event_type.startsWith('IO.') ||
        ['MED.ADMIN', 'NOTE.PROGRESS'].includes(e.event_type)) &&
      (!birthAt || Date.parse(e.occurred_at) >= Date.parse(birthAt)),
  );
  const grouped = new Map<string, ClinicalEvent[]>();
  for (const e of relevant) {
    const key = `${e.encounter_id}:${e.occurred_at}`;
    grouped.set(key, [...(grouped.get(key) || []), e]);
  }
  return [...grouped]
    .sort((a, b) => Date.parse(b[1][0].occurred_at) - Date.parse(a[1][0].occurred_at))
    .map(([key, group]) => {
      const p = (type: string) =>
        group.filter((e) => e.event_type === type).sort((a, b) => b.sequence - a.sequence)[0]?.payload ?? {};
      const bp = p('VITALS.BP'),
        hr = p('VITALS.PULSE'),
        rr = p('VITALS.RR'),
        temp = p('VITALS.TEMP'),
        spo = p('VITALS.SPO2'),
        f = p('PP.FUNDUS'),
        per = p('PP.PERINEUM'),
        loss = p('PP.BLOOD_LOSS');
      const val = (v: unknown) => (v === undefined || v === null ? '' : String(v));
      const vitals =
        [
          bp.systolic !== undefined && bp.diastolic !== undefined ? `${bp.systolic}/${bp.diastolic} mmHg` : '',
          hr.rate !== undefined ? `${hr.rate}/min` : '',
          rr.rate !== undefined ? `RR ${rr.rate}/min ${val(rr.quality)}` : '',
          temp.value_c !== undefined ? `${temp.value_c} °C ${val(temp.method)}` : '',
          spo.value_pct !== undefined ? `SpO₂ ${spo.value_pct}%` : '',
        ]
          .filter(Boolean)
          .join(' · ') || '—';
      const fb = f.relation_to_umbilicus_fb;
      const fundus =
        [typeof fb === 'number' ? `U${fb > 0 ? '+' : ''}${fb || ''}` : '', f.tone, f.deviation, per.status]
          .filter(Boolean)
          .join(' · ') || '—';
      const total = birthAt
        ? cumulativeBloodLoss(events, group[0].episode_id, subjectId, birthAt, group[0].occurred_at).value.total_ml
        : null;
      return {
        key,
        at: group[0].occurred_at,
        events: group,
        vitals,
        fundus,
        shock: shockIndex(hr.rate, bp.systolic).value,
        color: [p('NB.COLOR').value, p('NB.LOCATION').value].filter(Boolean).join(' · ') || '—',
        loss:
          [loss.amount_ml !== undefined ? `+${loss.amount_ml} mL` : '', loss.method, loss.descriptor]
            .filter(Boolean)
            .join(' · ') || '—',
        total,
        feeding:
          group
            .filter((e) => ['NB.FEEDING', 'IO.OUTPUT', 'IO.INTAKE'].includes(e.event_type))
            .map((e) => Object.values(e.payload).join(' · '))
            .join('; ') || '—',
        initials: [...new Set(group.map((e) => e.author_initials))].join(', '),
      };
    });
}

function readableFinding(value: unknown): string {
  if (value === null || value === undefined) return 'Not recorded';
  if (Array.isArray(value)) return value.map(readableFinding).join('; ');
  if (typeof value === 'object')
    return Object.entries(value as Record<string, unknown>)
      .map(
        ([key, v]) =>
          `${key.replaceAll('_', ' ')}: ${key === 'normal' && typeof v === 'boolean' ? (v ? 'Normal' : 'Abnormal') : readableFinding(v)}`,
      )
      .join(' · ');
  return typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value);
}
